import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  MenuItem,
  FormControlLabel,
  Checkbox,
  LinearProgress,
  Alert,
  Stack,
  Divider,
  Autocomplete,
  CircularProgress,
  FormGroup,
  ToggleButtonGroup,
  ToggleButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import {
  LocalShipping as ShippingIcon,
  Download as DownloadIcon,
  ViewList as BulkIcon,
  Place as AddressIcon,
} from '@mui/icons-material';
import api from '../../utils/axios';

export default function DpdCalculator() {
  const [mode, setMode] = useState('single');

  const [filters, setFilters] = useState({ regions: [], types: [] });
  const [region, setRegion] = useState('');
  const [type, setType] = useState('');
  const [pickupCity, setPickupCity] = useState(null);
  const [pickupInput, setPickupInput] = useState('');
  const [pickupOptions, setPickupOptions] = useState([]);
  const [pickupLoading, setPickupLoading] = useState(false);

  const [destCity, setDestCity] = useState(null);
  const [destInput, setDestInput] = useState('');
  const [destOptions, setDestOptions] = useState([]);
  const [destLoading, setDestLoading] = useState(false);
  const [destStreet, setDestStreet] = useState('');

  const [weight, setWeight] = useState('1');
  const [volume, setVolume] = useState('');
  const [cargoLength, setCargoLength] = useState('');
  const [cargoWidth, setCargoWidth] = useState('');
  const [cargoHeight, setCargoHeight] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [declaredValue, setDeclaredValue] = useState('');

  const [useDpd, setUseDpd] = useState(true);
  const [useDellin, setUseDellin] = useState(false);
  const [selfPickup, setSelfPickup] = useState(false);
  const [selfDelivery, setSelfDelivery] = useState(false);

  useEffect(() => {
    const l = parseFloat(cargoLength);
    const w = parseFloat(cargoWidth);
    const h = parseFloat(cargoHeight);
    if (l > 0 && w > 0 && h > 0) {
      setVolume((l * w * h).toFixed(4).replace(/0+$/, '').replace(/\.$/, ''));
    }
  }, [cargoLength, cargoWidth, cargoHeight]);

  const [countInfo, setCountInfo] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [progress, setProgress] = useState(null);
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(false);
  const pollRef = useRef(null);
  const pickupTimerRef = useRef(null);
  const destTimerRef = useRef(null);

  const [singleResult, setSingleResult] = useState(null);
  const [singleLoading, setSingleLoading] = useState(false);
  const [singleError, setSingleError] = useState(null);

  useEffect(() => {
    api.get('/dpd/filters').then(r => setFilters(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (pickupTimerRef.current) clearTimeout(pickupTimerRef.current);
    if (!pickupInput || pickupInput.length < 2) {
      setPickupOptions([]);
      return;
    }
    setPickupLoading(true);
    pickupTimerRef.current = setTimeout(() => {
      api.get('/dpd/pickup-search', { params: { q: pickupInput } })
        .then(r => setPickupOptions(r.data))
        .catch(() => setPickupOptions([]))
        .finally(() => setPickupLoading(false));
    }, 300);
    return () => clearTimeout(pickupTimerRef.current);
  }, [pickupInput]);

  useEffect(() => {
    if (destTimerRef.current) clearTimeout(destTimerRef.current);
    if (!destInput || destInput.length < 2) {
      setDestOptions([]);
      return;
    }
    setDestLoading(true);
    destTimerRef.current = setTimeout(() => {
      api.get('/dpd/destination-search', { params: { q: destInput } })
        .then(r => setDestOptions(r.data))
        .catch(() => setDestOptions([]))
        .finally(() => setDestLoading(false));
    }, 300);
    return () => clearTimeout(destTimerRef.current);
  }, [destInput]);

  const carriers = [useDpd && 'dpd', useDellin && 'dellin'].filter(Boolean);

  const fetchCount = useCallback(() => {
    if (mode !== 'bulk') return;
    const params = {};
    if (region) params.region = region;
    if (type) params.type = type;
    if (carriers.length) params.carriers = carriers.join(',');
    api.get('/dpd/count', { params }).then(r => setCountInfo(r.data)).catch(() => setCountInfo(null));
  }, [region, type, useDpd, useDellin, mode]);

  useEffect(() => {
    fetchCount();
  }, [fetchCount]);

  const buildCargoBody = () => {
    const body = {
      pickupCity: pickupCity?.city_name,
      pickupCityId: pickupCity?.city_id,
      weight: parseFloat(weight),
      selfPickup,
      selfDelivery,
      carriers,
    };
    if (volume) body.volume = parseFloat(volume);
    if (declaredValue) body.declaredValue = parseFloat(declaredValue);
    if (cargoLength) body.length = parseFloat(cargoLength);
    if (cargoWidth) body.width = parseFloat(cargoWidth);
    if (cargoHeight) body.height = parseFloat(cargoHeight);
    if (quantity && parseInt(quantity) > 1) body.quantity = parseInt(quantity);
    return body;
  };

  const handleStartBulk = async () => {
    if (!pickupCity || !weight || !carriers.length) return;
    setRunning(true);
    setDone(false);
    setProgress(null);
    setJobId(null);

    try {
      const body = { ...buildCargoBody() };
      if (region) body.region = region;
      if (type) body.type = type;

      const { data } = await api.post('/dpd/calculate', body);
      setJobId(data.jobId);
      startPolling(data.jobId);
    } catch {
      setRunning(false);
    }
  };

  const handleStartSingle = async () => {
    if (!pickupCity || !weight || !carriers.length || !destCity) return;
    setSingleLoading(true);
    setSingleResult(null);
    setSingleError(null);

    try {
      const body = {
        ...buildCargoBody(),
        destCityId: destCity.city_id,
        destCityName: destCity.city_name,
        destRegion: destCity.region,
        destStreet: destStreet.trim() || undefined,
      };
      const { data } = await api.post('/dpd/calculate-single', body);
      setSingleResult(data);
    } catch (err) {
      setSingleError(err.response?.data?.message || 'Ошибка расчёта');
    } finally {
      setSingleLoading(false);
    }
  };

  const startPolling = (id) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/dpd/calculate/${id}/status`);
        setProgress(data);
        if (data.status === 'done' || data.status === 'error') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setRunning(false);
          if (data.status === 'done') setDone(true);
        }
      } catch {
        clearInterval(pollRef.current);
        pollRef.current = null;
        setRunning(false);
      }
    }, 3000);
  };

  useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const handleDownload = () => {
    if (!jobId) return;
    const token = localStorage.getItem('token');
    const base = api.defaults.baseURL || '/api';
    fetch(`${base}/dpd/calculate/${jobId}/download`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `Доставка_${new Date().toISOString().slice(0, 10)}.xlsx`;
        a.click();
        URL.revokeObjectURL(a.href);
      });
  };

  const autoVolume = parseFloat(cargoLength) > 0 && parseFloat(cargoWidth) > 0 && parseFloat(cargoHeight) > 0;

  return (
    <Box sx={{ p: 3, maxWidth: 780, mx: 'auto' }}>
      <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ShippingIcon /> Калькулятор стоимости доставки
      </Typography>

      <Paper sx={{ p: 3, mt: 2 }}>
        <Stack spacing={2.5}>

          <ToggleButtonGroup
            value={mode} exclusive
            onChange={(_, v) => { if (v) setMode(v); }}
            size="small" fullWidth
          >
            <ToggleButton value="single"><AddressIcon sx={{ mr: 1 }} /> По адресу</ToggleButton>
            <ToggleButton value="bulk"><BulkIcon sx={{ mr: 1 }} /> Массовый расчёт</ToggleButton>
          </ToggleButtonGroup>

          <Divider />
          <Typography variant="subtitle2" color="text.secondary">Транспортные компании</Typography>
          <FormGroup row>
            <FormControlLabel
              control={<Checkbox checked={useDpd} onChange={e => setUseDpd(e.target.checked)} />}
              label="DPD"
            />
            <FormControlLabel
              control={<Checkbox checked={useDellin} onChange={e => setUseDellin(e.target.checked)} />}
              label="Деловые Линии"
            />
          </FormGroup>

          {!useDpd && !useDellin && (
            <Alert severity="warning" variant="outlined">Выберите хотя бы одну ТК</Alert>
          )}

          {mode === 'bulk' && (
            <>
              <Divider />
              <Typography variant="subtitle2" color="text.secondary">Фильтры географии</Typography>

              <TextField
                select label="Регион" value={region}
                onChange={e => setRegion(e.target.value)} size="small" fullWidth
              >
                <MenuItem value="">Все регионы</MenuItem>
                {filters.regions.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
              </TextField>

              <TextField
                select label="Тип населённого пункта" value={type}
                onChange={e => setType(e.target.value)} size="small" fullWidth
              >
                <MenuItem value="">Все типы</MenuItem>
                {filters.types.map(t => <MenuItem key={t.code} value={t.code}>{t.label}</MenuItem>)}
              </TextField>

              {countInfo && (
                <Alert severity={countInfo.count > 5000 ? 'warning' : 'info'} variant="outlined">
                  Населённых пунктов: <b>{countInfo.count.toLocaleString('ru-RU')}</b>
                  {countInfo.count > 0 && (
                    <> &mdash; примерное время расчёта: <b>
                      {countInfo.estimateMinutes < 1
                        ? `${countInfo.estimateSeconds} сек`
                        : `${countInfo.estimateMinutes} мин`}
                    </b></>
                  )}
                  {countInfo.count > 5000 && (
                    <Box sx={{ mt: 0.5 }}>Рекомендуем сузить фильтры для ускорения расчёта.</Box>
                  )}
                </Alert>
              )}
            </>
          )}

          <Divider />
          <Typography variant="subtitle2" color="text.secondary">Параметры груза</Typography>

          <Autocomplete
            options={pickupOptions}
            getOptionLabel={o => o.label || ''}
            isOptionEqualToValue={(o, v) => o.city_id === v.city_id}
            value={pickupCity}
            onChange={(_, v) => setPickupCity(v)}
            inputValue={pickupInput}
            onInputChange={(_, v) => setPickupInput(v)}
            loading={pickupLoading}
            noOptionsText={pickupInput.length < 2 ? 'Введите минимум 2 символа' : 'Не найдено'}
            filterOptions={x => x}
            renderInput={params => (
              <TextField
                {...params} label="Город отправления" size="small" required
                slotProps={{
                  input: {
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {pickupLoading ? <CircularProgress color="inherit" size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  },
                }}
              />
            )}
            fullWidth
          />

          {mode === 'single' && (
            <>
              <Autocomplete
                options={destOptions}
                getOptionLabel={o => o.label || ''}
                isOptionEqualToValue={(o, v) => o.city_id === v.city_id}
                value={destCity}
                onChange={(_, v) => setDestCity(v)}
                inputValue={destInput}
                onInputChange={(_, v) => setDestInput(v)}
                loading={destLoading}
                noOptionsText={destInput.length < 2 ? 'Введите минимум 2 символа' : 'Не найдено'}
                filterOptions={x => x}
                renderInput={params => (
                  <TextField
                    {...params} label="Город назначения" size="small" required
                    slotProps={{
                      input: {
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {destLoading ? <CircularProgress color="inherit" size={18} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      },
                    }}
                  />
                )}
                fullWidth
              />
              <TextField
                label="Улица, дом (необязательно)"
                value={destStreet}
                onChange={e => setDestStreet(e.target.value)}
                size="small" fullWidth
                placeholder="ул. Ленина, д. 1"
                helperText="Для более точного расчёта ДЛ"
              />
            </>
          )}

          <Stack direction="row" spacing={2}>
            <TextField
              label="Вес, кг" value={weight} onChange={e => setWeight(e.target.value)}
              size="small" type="number" inputProps={{ min: 0.1, step: 0.1 }} required sx={{ flex: 1 }}
            />
            <TextField
              label="Кол-во мест" value={quantity} onChange={e => setQuantity(e.target.value)}
              size="small" type="number" inputProps={{ min: 1, step: 1 }} sx={{ flex: 1 }}
            />
            <TextField
              label="Объявл. ценность, ₽" value={declaredValue} onChange={e => setDeclaredValue(e.target.value)}
              size="small" type="number" inputProps={{ min: 0 }} sx={{ flex: 1 }}
            />
          </Stack>

          <Stack direction="row" spacing={2}>
            <TextField
              label="Длина, м" value={cargoLength} onChange={e => setCargoLength(e.target.value)}
              size="small" type="number" inputProps={{ min: 0.01, step: 0.01 }} sx={{ flex: 1 }}
            />
            <TextField
              label="Ширина, м" value={cargoWidth} onChange={e => setCargoWidth(e.target.value)}
              size="small" type="number" inputProps={{ min: 0.01, step: 0.01 }} sx={{ flex: 1 }}
            />
            <TextField
              label="Высота, м" value={cargoHeight} onChange={e => setCargoHeight(e.target.value)}
              size="small" type="number" inputProps={{ min: 0.01, step: 0.01 }} sx={{ flex: 1 }}
            />
            <TextField
              label="Объём, м³" value={volume}
              onChange={e => setVolume(e.target.value)}
              size="small" type="number"
              inputProps={{ min: 0.001, step: 0.001, readOnly: autoVolume }}
              helperText={autoVolume ? 'Авто из габаритов' : ''}
              sx={{ flex: 1 }}
            />
          </Stack>

          <Typography variant="subtitle2" color="text.secondary">Условия отправки / получения</Typography>
          <Stack direction="row" spacing={2}>
            <FormControlLabel
              control={<Checkbox checked={selfPickup} onChange={e => setSelfPickup(e.target.checked)} size="small" />}
              label="Самопривоз на терминал"
            />
            <FormControlLabel
              control={<Checkbox checked={selfDelivery} onChange={e => setSelfDelivery(e.target.checked)} size="small" />}
              label="Самовывоз с терминала"
            />
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ mt: -1.5 }}>
            Если не отмечено — забор от двери / доставка до двери
          </Typography>

          <Divider />

          {mode === 'single' ? (
            <Button
              variant="contained" onClick={handleStartSingle} size="large"
              disabled={singleLoading || !pickupCity || !weight || !carriers.length || !destCity}
            >
              {singleLoading ? <><CircularProgress size={20} sx={{ mr: 1 }} color="inherit" /> Расчёт...</> : 'Рассчитать'}
            </Button>
          ) : (
            <Button
              variant="contained" onClick={handleStartBulk} size="large"
              disabled={running || !pickupCity || !weight || !carriers.length || (countInfo && countInfo.count === 0)}
            >
              {running ? 'Расчёт...' : 'Рассчитать стоимость'}
            </Button>
          )}

          {/* Результаты одиночного расчёта */}
          {mode === 'single' && singleResult && (
            <Box>
              {singleResult.errors?.length > 0 && singleResult.errors.map((e, i) => (
                <Alert key={i} severity="warning" sx={{ mb: 1 }}>
                  {e.carrier}: {e.message}
                </Alert>
              ))}

              {singleResult.results?.dpd?.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    <Chip label="DPD" size="small" color="primary" sx={{ mr: 1 }} /> Варианты доставки
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Услуга</TableCell>
                          <TableCell>Код</TableCell>
                          <TableCell align="right">Стоимость, ₽</TableCell>
                          <TableCell align="right">Срок, дней</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {singleResult.results.dpd.map((r, i) => (
                          <TableRow key={i}>
                            <TableCell>{r.serviceName}</TableCell>
                            <TableCell>{r.serviceCode}</TableCell>
                            <TableCell align="right"><b>{r.cost?.toLocaleString('ru-RU', { minimumFractionDigits: 2 })}</b></TableCell>
                            <TableCell align="right">{r.days || '—'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {singleResult.results?.dellin?.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    <Chip label="Деловые Линии" size="small" color="secondary" sx={{ mr: 1 }} /> Варианты доставки
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Вид перевозки</TableCell>
                          <TableCell>Код</TableCell>
                          <TableCell align="right">Стоимость, ₽</TableCell>
                          <TableCell align="right">Срок, дней</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {singleResult.results.dellin.map((r, i) => (
                          <TableRow key={i}>
                            <TableCell>{r.serviceName}</TableCell>
                            <TableCell>{r.serviceCode}</TableCell>
                            <TableCell align="right"><b>{r.cost?.toLocaleString('ru-RU', { minimumFractionDigits: 2 })}</b></TableCell>
                            <TableCell align="right">{r.days || '—'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {!singleResult.results?.dpd?.length && !singleResult.results?.dellin?.length && !singleResult.errors?.length && (
                <Alert severity="info">Нет доступных вариантов доставки</Alert>
              )}
            </Box>
          )}

          {mode === 'single' && singleError && (
            <Alert severity="error">{singleError}</Alert>
          )}

          {/* Прогресс массового расчёта */}
          {mode === 'bulk' && running && progress && (
            <Box>
              <LinearProgress variant="determinate" value={progress.percent || 0} sx={{ height: 8, borderRadius: 1 }} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Обработано {progress.progress} из {progress.total} ({progress.percent}%)
                {progress.errorCount > 0 && ` • Ошибок: ${progress.errorCount}`}
              </Typography>
            </Box>
          )}

          {mode === 'bulk' && done && (
            <Alert
              severity="success"
              action={
                <Button color="inherit" size="small" startIcon={<DownloadIcon />} onClick={handleDownload}>
                  Скачать Excel
                </Button>
              }
            >
              Расчёт завершён!
              {progress && progress.errorCount > 0 && ` (ошибок: ${progress.errorCount})`}
            </Alert>
          )}

          {mode === 'bulk' && progress && progress.status === 'error' && (
            <Alert severity="error">Ошибка расчёта: {progress.error}</Alert>
          )}
        </Stack>
      </Paper>
    </Box>
  );
}
